import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Send, CheckCircle2, Loader2, BadgeCheck, Globe, Linkedin } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { toast } from 'sonner';

const COMMON_LANGUAGES = ['English', 'Français', 'Spanish', 'Vietnamese', 'Mandarin', 'Portuguese', 'Arabic'];

export default function SpecialistApplyPage() {
  const [params] = useSearchParams();
  const token = params.get('invite') || '';
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    title: '', bio: '', languages: [], certifications: '',
    years_experience: '', linkedin_url: '', pitch: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setError('This page requires an invitation link.'); setLoading(false); return; }
    api.lookupInvitation(token).then((d) => {
      if (d.kind !== 'specialist') { setError('That invitation is not for a Tax Specialist application.'); return; }
      if (d.expired) { setError('This invitation has expired. Please ask for a new one.'); return; }
      if (d.status === 'submitted') { setError('We already received your application — thank you!'); return; }
      if (d.status === 'accepted' || d.status === 'revoked') { setError('This invitation is no longer valid.'); return; }
      setInvite(d);
      setForm((f) => ({ ...f, email: d.email, first_name: (d.name || '').split(' ')[0] || '', last_name: (d.name || '').split(' ').slice(1).join(' ') }));
    }).catch(() => setError('Invitation link is invalid.'))
      .finally(() => setLoading(false));
  }, [token]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleLang = (lang) => update({
    languages: form.languages.includes(lang)
      ? form.languages.filter((l) => l !== lang)
      : [...form.languages, lang],
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.bio || form.bio.length < 10) {
      toast.error('Please complete first name, email and a 10+ character bio');
      return;
    }
    setSubmitting(true);
    try {
      await api.submitSpecialistApplication({
        invite_token: token,
        ...form,
        years_experience: form.years_experience ? parseInt(form.years_experience, 10) || null : null,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit application');
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <Header />
      <section className="py-12 bg-gradient-to-b from-[#fbf7ee] to-white border-b border-neutral-100">
        <div className="max-w-[760px] mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] text-neutral-500 hover:text-[#c89200] mb-4">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to wysetribe.ca
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#f5b300]/15 text-[#c89200] flex items-center justify-center">
              <BadgeCheck className="w-5 h-5" />
            </div>
            <div>
              <p className="section-eyebrow mb-1">Join WyseTribe</p>
              <h1 className="text-[28px] md:text-[32px] font-extrabold text-[#14110f] leading-tight">Apply to become a Tax Specialist</h1>
              <p className="text-[13.5px] text-neutral-600 mt-1">
                {invite?.invited_by_name
                  ? <>Invited by <strong>{invite.invited_by_name}</strong>. Tell us a bit about you and we&rsquo;ll review within 5 business days.</>
                  : 'Tell us a bit about you and we\u2019ll review within 5 business days.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-[760px] mx-auto px-6">
          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-[#c89200] mx-auto" />
            </div>
          ) : error ? (
            <div data-testid="apply-error" className="rounded-xl border border-rose-100 bg-rose-50 text-rose-700 p-6 text-center">
              <p className="text-[15px] font-bold">{error}</p>
              <p className="text-[13px] mt-1">If you believe this is wrong, contact <a href="mailto:info@wysetribe.ca" className="underline">info@wysetribe.ca</a>.</p>
            </div>
          ) : submitted ? (
            <div data-testid="apply-success" className="rounded-xl border border-emerald-100 bg-emerald-50 text-emerald-800 p-7 text-center">
              <CheckCircle2 className="w-10 h-10 mx-auto" />
              <h2 className="text-[22px] font-extrabold mt-2">Application received</h2>
              <p className="text-[14px] mt-2 max-w-md mx-auto">
                Thanks {form.first_name}! We&rsquo;ve sent a confirmation to <strong>{form.email}</strong>. Our team will review and reach out within 5 business days.
              </p>
            </div>
          ) : (
            <form data-testid="specialist-application-form" onSubmit={submit}
                  className="rounded-xl border border-neutral-200 bg-white p-6 md:p-7 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="First name" required value={form.first_name} onChange={(v) => update({ first_name: v })} testid="app-first" />
                <Field label="Last name" value={form.last_name} onChange={(v) => update({ last_name: v })} testid="app-last" />
              </div>
              <Field label="Email" required value={form.email} readOnly testid="app-email" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Phone" value={form.phone} onChange={(v) => update({ phone: v })} testid="app-phone" />
                <Field label="Title" value={form.title} onChange={(v) => update({ title: v })}
                       placeholder="e.g. Senior Tax Specialist" testid="app-title" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Years of experience" type="number" min="0"
                       value={form.years_experience} onChange={(v) => update({ years_experience: v })} testid="app-years" />
                <Field label="LinkedIn URL" value={form.linkedin_url} onChange={(v) => update({ linkedin_url: v })}
                       placeholder="https://linkedin.com/in/..." testid="app-linkedin" icon={Linkedin} />
              </div>
              <div>
                <p className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5" />Languages spoken
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {COMMON_LANGUAGES.map((lang) => (
                    <button key={lang} type="button"
                            data-testid={`app-lang-${lang}`}
                            onClick={() => toggleLang(lang)}
                            className={`px-3 py-1.5 rounded-md text-[12.5px] font-semibold transition ${
                              form.languages.includes(lang)
                                ? 'bg-[#14110f] text-white'
                                : 'bg-white border border-neutral-200 text-neutral-600 hover:border-[#f5b300]'
                            }`}>
                      {lang}
                    </button>
                  ))}
                </div>
              </div>
              <TextArea label="Bio" required rows={4}
                        placeholder="A short story about your tax career — minimum 10 characters."
                        value={form.bio} onChange={(v) => update({ bio: v })} testid="app-bio" />
              <TextArea label="Certifications (optional)" rows={2}
                        placeholder="CPA, CFP, EA, RPA..."
                        value={form.certifications} onChange={(v) => update({ certifications: v })} testid="app-certs" />
              <TextArea label="Why WyseTribe? (optional)" rows={3}
                        placeholder="What makes you a great fit for our team."
                        value={form.pitch} onChange={(v) => update({ pitch: v })} testid="app-pitch" />
              <div className="border-t border-neutral-100 pt-4 flex items-center justify-end gap-2">
                <button type="submit" data-testid="app-submit-btn" disabled={submitting}
                        className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] disabled:opacity-60 text-[#14110f] font-semibold px-6 py-3 rounded-md text-[13.5px]">
                  <Send className="w-4 h-4" />{submitting ? 'Submitting\u2026' : 'Submit application'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}

function Field({ label, value, onChange, required, type = 'text', placeholder, testid, readOnly, icon: Icon, ...rest }) {
  return (
    <label className="block">
      <span className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">
        {label}{required && <span className="text-rose-500"> *</span>}
      </span>
      <div className="mt-1 relative">
        {Icon && <Icon className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />}
        <input
          {...rest} type={type} value={value || ''} readOnly={readOnly}
          onChange={(e) => onChange && onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          required={required}
          className={`w-full ${Icon ? 'pl-9' : 'pl-3'} pr-3 py-2.5 rounded-md border text-[14px] focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none ${readOnly ? 'bg-neutral-50 text-neutral-500 border-neutral-200' : 'border-neutral-200'}`}
        />
      </div>
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 3, placeholder, testid, required }) {
  return (
    <label className="block">
      <span className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">
        {label}{required && <span className="text-rose-500"> *</span>}
      </span>
      <textarea rows={rows} value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder} required={required}
                data-testid={testid}
                className="mt-1 w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none" />
    </label>
  );
}
